import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AssetDto } from '../../../../../../../api/src/asset/models/asset.dto';
import { ImageActionType } from '../../../../../../../api/src/asset/models/image-action.dto';
import { ImagePickerActionsDto } from '../../../../../../../api/src/creative-unit-package/models/customization/customization.dto';
import { ImageEditorService } from '../../image-editor.service';
import { MatDialog } from '@angular/material/dialog';
import { AssetPickerDialogComponent } from '../../../../_core/state/asset/asset-picker-dialog/asset-picker-dialog.component';
import { Type } from '../../../../../../../api/src/asset/asset.entity';
import { AiConversationConfig, AiMessage, AiModelRef } from '../../../ai/models';
import { uploadDataSource } from '../../../../_core/state/data-source/data-source.data';
import { AssetCardLayouts } from '../../../../_core/state/asset/asset.data';

@Component({
	selector: 'app-image-tool-edit-with-ai',
	templateUrl: './image-tool-edit-with-ai.component.html',
	styleUrls: ['./image-tool-edit-with-ai.component.scss']
})
export class ImageToolEditWithAiComponent implements OnInit {
	@Input() public activeAsset: AssetDto;
	@Input() tools: ImageActionType[];
	@Input() config: ImagePickerActionsDto;
	@Output() setActiveAsset: EventEmitter<AssetDto> = new EventEmitter<AssetDto>();
	@Output() setActiveVariations: EventEmitter<AssetDto[]> = new EventEmitter<AssetDto[]>();
	@Output() setLoadingMessage: EventEmitter<string | null> = new EventEmitter<string | null>();

	public ImageActionType = ImageActionType;
	public conversationId: string;
	public selectedReferenceImages: AssetDto[] = [];
	public isLoading: boolean = false;
	public availableModels: any[] = [];
	public selectedModel: any = null;

	// AI Conversation Shell properties
	public conversationConfig: AiConversationConfig;
	public conversationMessages: AiMessage[] = [];
	public availableAiModels: AiModelRef[] = [];

	constructor(
		private imageEditorService: ImageEditorService,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		// Generate new conversation ID
		this.conversationId = this.generateConversationId();

		// Hardcode the single available model for image editing
		this.availableAiModels = [{
			id: 'gemini-3-pro-image-preview',
			label: 'Gemini 3 Pro Image',
			provider: 'google',
			capabilities: ['image']
		}];
		this.selectedModel = this.availableAiModels[0];

		// Initialize conversation config
		this.initializeConversationConfig();
	}

	/**
	 * Initialize AI Conversation Shell configuration
	 */
	private initializeConversationConfig(): void {
		this.conversationConfig = {
			title: 'Edit Image with AI',
			showClose: false,
			description: 'Describe how you want to edit this image using natural language',
			composerPlaceholder: 'E.g., "Make the sky more dramatic" or "Add a sunset in the background"',
			loadingText: 'Editing your image...',
			footer: {
				showSettings: true,
				showBrandKit: true,
				showModelSelector: false, // Only one model available
				showReferenceImages: true // Always show for image-edit mode
			},
			uiVariant: 'sidebar',
			mode: 'image-edit',
			defaultModel: this.availableAiModels[0],
			availableModels: this.availableAiModels, // Pass available models to conversation shell
			hidePhotoGridRenderer: true, // Don't show photo grid in transcript - image is shown in preview window
			media: {
				results: 1,
				styleType: 'Auto',
				referenceImages: this.selectedReferenceImages.map(img => ({ url: img.assetPath, id: img.id })),
				maxReferenceImages: 14
			}
		};
	}

	/**
	 * Generate a unique conversation ID
	 */
	private generateConversationId(): string {
		return `conv-${Date.now()}-${Math.random().toString(36).substring(7)}`;
	}

	/**
	 * Open asset picker to select an image to edit
	 */
	openAssetPicker(): void {
		const dialogRef = this.dialog.open(AssetPickerDialogComponent, {
			data: {
				config: {
					datasource: { ...uploadDataSource },
					mediaType: Type.Image,
					ui: {
						layout: AssetCardLayouts.find(layout => layout.id === 'grid-6'),
						showUpload: true
					}
				}
			},
			panelClass: ['fullscreen', 'min-height', 'stretch', 'stretch-height']
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result && result.length > 0) {
				this.activeAsset = result[0];
				this.setActiveAsset.emit(this.activeAsset);
			}
		});
	}

	/**
	 * Open asset picker to select reference images (max 3)
	 */
	openReferenceImagePicker(): void {
		const dialogRef = this.dialog.open(AssetPickerDialogComponent, {
			data: {
				config: {
					datasource: { ...uploadDataSource },
					mediaType: Type.Image,
					ui: {
						layout: AssetCardLayouts.find(layout => layout.id === 'grid-6'),
						showUpload: true
					},
					selectedItems: this.selectedReferenceImages,
					multiSelect: true,
					maxSelect: 3
				}
			},
			panelClass: ['fullscreen', 'min-height', 'stretch', 'stretch-height']
		});

		dialogRef.afterClosed().subscribe(result => {
			console.log('Reference image picker closed with result:', result);
			if (result && result.length > 0) {
				this.selectedReferenceImages = [...this.selectedReferenceImages, ...result];
				// Limit to 3 reference images
				this.selectedReferenceImages = this.selectedReferenceImages.slice(0, 3);

				console.log('Updated selectedReferenceImages:', this.selectedReferenceImages);

				// Update conversation config with new reference images
				// Trigger change detection by creating new object reference
				this.conversationConfig = {
					...this.conversationConfig,
					media: {
						...this.conversationConfig.media,
						referenceImages: this.selectedReferenceImages.map(img => ({
							url: img.assetPath,
							id: img.id
						}))
					}
				};

				console.log('Updated conversationConfig:', this.conversationConfig);
			}
		});
	}

	/**
	 * Remove a reference image from the selection
	 */
	removeReferenceImage(index: number): void {
		this.selectedReferenceImages.splice(index, 1);
		console.log('Removed reference image at index:', index, 'Remaining:', this.selectedReferenceImages);

		// Update conversation config - trigger change detection by creating new object reference
		this.conversationConfig = {
			...this.conversationConfig,
			media: {
				...this.conversationConfig.media,
				referenceImages: this.selectedReferenceImages.map(img => ({
					url: img.assetPath,
					id: img.id
				}))
			}
		};
	}

	/**
	 * Clear all reference images from the selection
	 */
	private clearReferenceImages(): void {
		this.selectedReferenceImages = [];
		console.log('Cleared all reference images');

		// Update conversation config - trigger change detection by creating new object reference
		this.conversationConfig = {
			...this.conversationConfig,
			media: {
				...this.conversationConfig.media,
				referenceImages: []
			}
		};
	}

	/**
	 * Handle send message event from AI Conversation Shell
	 */
	handleSendMessage(prompt: string): void {
		if (!this.activeAsset || !prompt?.trim()) {
			return;
		}

		this.isLoading = true;
		this.setLoadingMessage.emit('Editing your image...');

		// Add user message to conversation - include reference images if present
		const userMsg: AiMessage = {
			id: `msg-${Date.now()}-user`,
			role: 'user',
			type: 'text',
			content: prompt,
			meta: {
				referenceImages: this.selectedReferenceImages.length > 0
					? this.selectedReferenceImages.map(img => ({ url: img.assetPath, id: img.id }))
					: undefined
			}
		};
		this.conversationMessages = [...this.conversationMessages, userMsg];

		// Build API request
		const request: any = {
			prompt,
			imageUrl: this.activeAsset.assetPath,
			referenceImageUrls: this.selectedReferenceImages.map(img => img.assetPath),
			conversationHistory: this.buildConversationHistory(),
			conversationId: this.conversationId,
			model: this.selectedModel?.id
		};

		// Call API
		this.imageEditorService.editImageWithText(request)
			.subscribe(
				response => {
					this.isLoading = false;
					this.setLoadingMessage.emit(null);

					// Add assistant response to conversation
					const assistantMsg: AiMessage = {
						id: `msg-${Date.now()}-assistant`,
						role: 'assistant',
						type: 'photo-grid',
						content: [{ url: response.asset.assetPath }],
						meta: {
							assetId: response.asset.id,
							prompt
						}
					};
					this.conversationMessages = [...this.conversationMessages, assistantMsg];

					// Update conversation ID from response
					this.conversationId = response.conversationId;

					// Update local active asset so next edit builds on this one
					this.activeAsset = response.asset;
					console.log('AI Edit: Updated activeAsset:', this.activeAsset);

					// Emit new asset as variation (prepends to variation tray and adds to undo stack)
					console.log('AI Edit: Emitting new variation:', response.asset);
					this.setActiveVariations.emit([response.asset]);

					// Clear reference images after successful edit
					this.clearReferenceImages();
				},
				error => {
					this.isLoading = false;
					this.setLoadingMessage.emit(null);
					console.error('Error editing image:', error);

					// Add error message to conversation
					const errorMsg: AiMessage = {
						id: `msg-${Date.now()}-error`,
						role: 'assistant',
						type: 'error',
						content: error?.error?.userMessage || error?.message || 'Failed to edit image. Please try again.'
					};
					this.conversationMessages = [...this.conversationMessages, errorMsg];
				}
			);
	}

	/**
	 * Handle model change event from AI Conversation Shell
	 */
	handleModelChange(modelId: string): void {
		const model = this.availableModels.find(m => m.id === modelId);
		if (model) {
			this.selectedModel = model;
		}
	}

	/**
	 * Build conversation history for API request
	 */
	private buildConversationHistory(): Array<{ role: string; prompt: string; imageUrl?: string }> {
		const history = [];

		for (let i = 0; i < this.conversationMessages.length; i++) {
			const msg = this.conversationMessages[i];

			if (msg.role === 'user') {
				history.push({
					role: 'user',
					prompt: msg.content
				});
			} else if (msg.role === 'assistant' && msg.type === 'photo-grid') {
				const imageUrl = msg.content?.[0]?.url;
				if (imageUrl) {
					history.push({
						role: 'assistant',
						prompt: msg.meta?.prompt || '',
						imageUrl
					});
				}
			}
		}

		return history;
	}

	/**
	 * Check if selected model supports reference images
	 */
	get selectedModelSupportsReferenceImages(): boolean {
		return this.selectedModel?.metadata?.maxReferenceImages > 0;
	}

	/**
	 * Check if max reference images selected
	 */
	get isMaxReferenceImagesSelected(): boolean {
		const maxImages = this.selectedModel?.metadata?.maxReferenceImages || 3;
		return this.selectedReferenceImages.length >= maxImages;
	}
}
